import React, { useState, useEffect } from 'react';
import styled from "styled-components";
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SideBarIVF from "./SideBarIVF";
import { FaArrowLeft, FaSave, FaTimes, FaCheckCircle, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';

const slugifyText = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
};

const AdminBlogForm = () => {
  const { id } = useParams(); // If editing existing blog, id will be present
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    canonical_url: '',
    image_alt: '',
    category: 'Fertility',
    author: 'Pluro Jabalpur Fertility Center',
    content: '',
    meta_keywords: '',
    is_published: true,
  });

  const [slugModifiedByUser, setSlugModifiedByUser] = useState(false);
  const [image, setImage] = useState(null);
  const [currentImage, setCurrentImage] = useState('');
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    // If editing, fetch the blog data
    if (isEditing) {
      const fetchBlog = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/blogs/blogGetForEditing/${id}`);
          const blogData = response.data;
          
          setFormData({
            title: blogData.title || '',
            slug: blogData.slug || '',
            meta_title: blogData.meta_title || blogData.title || '',
            meta_description: blogData.meta_description || '',
            canonical_url: blogData.canonical_url || (blogData.slug ? `https://plurojabalpurivfcentre.com/blogs/${blogData.slug}` : ''),
            image_alt: blogData.image_alt || blogData.title || '',
            category: blogData.category || 'Fertility',
            author: blogData.author || 'Pluro Jabalpur Fertility Center',
            content: blogData.content || '',
            meta_keywords: blogData.meta_keywords || '',
            is_published: Boolean(blogData.is_published),
          });

          setSlugModifiedByUser(true);
          
          if (blogData.image_url) {
            setCurrentImage(
              blogData.image_url.startsWith('http')
                ? blogData.image_url
                : `https://jabalpurivfcentre.com/${blogData.image_url.replace(/^\//, '')}`
            );
          }
          
          setLoading(false);
        } catch (err) {
          setError('Failed to load blog data');
          setLoading(false);
          toast.error('Failed to load blog data');
        }
      };

      fetchBlog();
    }
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'slug') {
      setSlugModifiedByUser(true);
    }

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      // 1. Slug URL MUST be generated from Meta Title
      if (name === 'meta_title') {
        const generatedSlug = slugifyText(value);
        updated.slug = generatedSlug;
        updated.canonical_url = generatedSlug 
          ? `https://plurojabalpurivfcentre.com/blogs/${generatedSlug}` 
          : '';
      }

      // 2. If Blog Title changes and Meta Title is empty or was same as Title, sync Meta Title and Slug
      if (name === 'title' && !isEditing) {
        if (!prev.meta_title || prev.meta_title === prev.title) {
          updated.meta_title = value;
          const generatedSlug = slugifyText(value);
          updated.slug = generatedSlug;
          updated.canonical_url = generatedSlug 
            ? `https://plurojabalpurivfcentre.com/blogs/${generatedSlug}` 
            : '';
        }
      }

      // 3. If user edits slug manually, keep Canonical URL in sync
      if (name === 'slug') {
        const cleanSlug = slugifyText(value);
        updated.canonical_url = cleanSlug 
          ? `https://plurojabalpurivfcentre.com/blogs/${cleanSlug}` 
          : '';
      }

      return updated;
    });
  };

  const handleContentChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      content,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewImage(null);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a Blog Title');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Please enter Blog Content');
      return;
    }

    setSaving(true);

    try {
      const formDataToSend = new FormData();
      
      // Append all form data
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Ensure Author is always set to Pluro Jabalpur Fertility Center if empty
      if (!formData.author) {
        formDataToSend.set('author', 'Pluro Jabalpur Fertility Center');
      }

      // Ensure Slug URL is generated from Meta Title (or Title if Meta Title is empty)
      const finalSlug = (formData.slug && formData.slug.trim())
        ? slugifyText(formData.slug.trim())
        : slugifyText(formData.meta_title || formData.title);
      formDataToSend.set('slug', finalSlug);

      // Ensure Canonical URL points to frontend website domain
      const finalCanonical = formData.canonical_url || (finalSlug ? `https://plurojabalpurivfcentre.com/blogs/${finalSlug}` : '');
      formDataToSend.set('canonical_url', finalCanonical);

      // Append image if selected
      if (image) {
        formDataToSend.append('image', image);
      }

      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/blogs/adminUpdateBlog/${id}`,
          formDataToSend,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success('Blog updated successfully');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/blogs/admin-create`,
          formDataToSend,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success('Blog created successfully');
      }

      navigate('/AdminAdd-blogs');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message;
      toast.error('Error saving blog: ' + errorMsg);
      console.error('Error saving blog:', error);
      setError('Error saving blog: ' + errorMsg);
      setSaving(false);
    }
  };

  if (loading && isEditing) {
    return (
      <Wrapper>
        <div className="main">
          <div className="container-fluid">
            <div className="row flex-nowrap">
              <div className="col-lg-1 col-1 p-0">
                <SideBarIVF />
              </div>
              <div className="col-lg-11 col-11 ps-0 mt-5">
                <div className="container">
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                    <div className="spinner-grow text-success me-2" role="status"></div>
                    <div className="spinner-grow text-primary me-2" role="status"></div>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="main">
        <div className="container-fluid">
          <div className="row flex-nowrap">
            <div className="col-lg-1 col-1 p-0">
              <SideBarIVF />
            </div>
            <div className="col-lg-11 col-11 ps-3 pe-4 py-3">
              <div className="container-fluid px-2">
                
                {error && (
                  <div className="alert alert-danger shadow-sm mb-4" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                {/* Main Card Container */}
                <div className="blog-form-card shadow-sm rounded-3 bg-white p-4 mb-4">
                  
                  {/* Top Bar matching user image design */}
                  <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                    <div>
                      <h3 className="form-header-title mb-0">{isEditing ? 'Edit Blog' : 'New Blog'}</h3>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Link to="/AdminAdd-blogs" className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-2">
                        <FaArrowLeft className="me-1" /> Back
                      </Link>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        className="btn btn-new-blog text-white px-3 py-2 rounded-2 d-flex align-items-center"
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <FaPlus className="me-1" /> {isEditing ? 'Update Blog' : '+ New Blog'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {/* Row 1: Blog Title & URL Slug */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label htmlFor="title" className="form-label fw-semibold text-secondary">
                          Blog Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control custom-input"
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          placeholder=""
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="slug" className="form-label fw-semibold text-secondary">
                          URL Slug
                        </label>
                        <input
                          type="text"
                          className="form-control custom-input"
                          id="slug"
                          name="slug"
                          value={formData.slug}
                          onChange={handleChange}
                          placeholder=""
                        />
                        <div className="field-hint text-muted mt-1">
                          Example: how-to-improve-google-rankings (Auto-generated from Meta Title)
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Meta Title & Meta Description */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label htmlFor="meta_title" className="form-label fw-semibold text-secondary">
                          Meta Title
                        </label>
                        <input
                          type="text"
                          className="form-control custom-input"
                          id="meta_title"
                          name="meta_title"
                          value={formData.meta_title}
                          onChange={handleChange}
                          placeholder=""
                        />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="meta_description" className="form-label fw-semibold text-secondary">
                          Meta Description
                        </label>
                        <textarea
                          className="form-control custom-input"
                          id="meta_description"
                          name="meta_description"
                          value={formData.meta_description}
                          onChange={handleChange}
                          rows="4"
                          placeholder=""
                        ></textarea>
                      </div>
                    </div>

                    {/* Row 3: Canonical URL */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label htmlFor="canonical_url" className="form-label fw-semibold text-secondary">
                          Canonical URL
                        </label>
                        <input
                          type="text"
                          className="form-control custom-input"
                          id="canonical_url"
                          name="canonical_url"
                          value={formData.canonical_url}
                          onChange={handleChange}
                          placeholder="https://plurojabalpurivfcentre.com/blogs/your-slug"
                        />
                        <div className="field-hint text-muted mt-1">
                          Example: https://plurojabalpurivfcentre.com/blogs/how-to-improve-google-rankings
                        </div>
                      </div>
                    </div>

                    {/* Row 4: Featured Image & Image Alt Text */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label htmlFor="image" className="form-label fw-semibold text-secondary">
                          Featured Image
                        </label>
                        <input
                          type="file"
                          className="form-control custom-input"
                          id="image"
                          name="image"
                          onChange={handleImageChange}
                          accept="image/jpeg,image/png,image/webp,image/gif,image/jpg"
                        />
                        <div className="field-hint text-muted mt-1">
                          JPG, PNG, WEBP or GIF. On publish it is stored inside /slug/images/.
                        </div>
                        {(previewImage || currentImage) && (
                          <div className="mt-2 d-flex align-items-center gap-2">
                            <img
                              src={previewImage || currentImage}
                              alt="preview"
                              style={{ maxHeight: "60px", maxWidth: "100px", borderRadius: "4px", objectFit: "cover" }}
                            />
                            {previewImage && <span className="text-success small"><FaCheckCircle /> New image selected</span>}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="image_alt" className="form-label fw-semibold text-secondary">
                          Image Alt Text
                        </label>
                        <input
                          type="text"
                          className="form-control custom-input"
                          id="image_alt"
                          name="image_alt"
                          value={formData.image_alt}
                          onChange={handleChange}
                          placeholder=""
                        />
                      </div>
                    </div>

                    {/* Row 5: Category & Author */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <label htmlFor="category" className="form-label fw-semibold text-secondary">
                          Category
                        </label>
                        <input
                          type="text"
                          className="form-control custom-input"
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          placeholder="e.g. Fertility, IVF Guide"
                        />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="author" className="form-label fw-semibold text-secondary">
                          Author
                        </label>
                        <input
                          type="text"
                          className="form-control custom-input fw-semibold"
                          id="author"
                          name="author"
                          value={formData.author}
                          onChange={handleChange}
                          placeholder="Pluro Jabalpur Fertility Center"
                          required
                        />
                      </div>
                    </div>

                    {/* Row 6: Meta Keywords (Optional) */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-12">
                        <label htmlFor="meta_keywords" className="form-label fw-semibold text-secondary">
                          Meta Keywords
                        </label>
                        <input
                          type="text"
                          className="form-control custom-input"
                          id="meta_keywords"
                          name="meta_keywords"
                          value={formData.meta_keywords}
                          onChange={handleChange}
                          placeholder="ivf treatment, fertility doctor, jabalpur"
                        />
                      </div>
                    </div>

                    {/* Blog Content with ReactQuill */}
                    <div className="mb-4">
                      <label className="form-label fw-semibold text-secondary">
                        Blog Content <span className="text-danger">*</span>
                      </label>
                      <div className="quill-wrapper">
                        <ReactQuill
                          value={formData.content}
                          onChange={handleContentChange}
                          theme="snow"
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, 3, 4, 5, 6, false] }],
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              ['link', 'image'],
                              ['clean']
                            ]
                          }}
                          style={{ height: '320px', marginBottom: '55px' }}
                        />
                      </div>
                    </div>

                    {/* Publish Status & Actions */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center pt-3 border-top mt-4">
                      <div className="form-check form-switch mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="is_published"
                          name="is_published"
                          checked={formData.is_published}
                          onChange={handleChange}
                        />
                        <label className="form-check-label fw-semibold ms-2" htmlFor="is_published">
                          {formData.is_published ? (
                            <span className="text-success">Published</span>
                          ) : (
                            <span className="text-secondary">Draft</span>
                          )}
                        </label>
                      </div>

                      <div className="d-flex gap-2 mb-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary px-4 py-2"
                          onClick={() => navigate('/AdminAdd-blogs')}
                        >
                          <FaTimes className="me-1" /> Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-success px-4 py-2 text-white fw-semibold"
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <FaSave className="me-2" />
                              {isEditing ? 'Update Blog' : 'Publish Blog'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default AdminBlogForm;

const Wrapper = styled.div`
  .main {
    min-height: 100vh;
    background-color: #f4f6f8;
    padding-bottom: 2rem;
  }

  .blog-form-card {
    border: 1px solid #e9ecef;
  }

  .form-header-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    font-family: inherit;
  }

  .btn-new-blog {
    background-color: #0f8b44;
    border-color: #0f8b44;
    font-weight: 500;
    transition: all 0.2s ease;
    &:hover {
      background-color: #0a6c34;
      border-color: #0a6c34;
    }
  }

  .custom-input {
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 0.55rem 0.75rem;
    font-size: 0.95rem;
    color: #1f2937;
    background-color: #ffffff;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
      border-color: #0f8b44;
      box-shadow: 0 0 0 3px rgba(15, 139, 68, 0.15);
    }
  }

  .field-hint {
    font-size: 0.78rem;
    color: #6b7280 !important;
  }

  .quill-wrapper {
    background: #ffffff;
    .ql-toolbar {
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
      border-color: #d1d5db;
    }
    .ql-container {
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
      border-color: #d1d5db;
      font-size: 1rem;
    }
  }

  .form-switch .form-check-input {
    width: 2.8em;
    height: 1.4em;
    cursor: pointer;
    &:checked {
      background-color: #0f8b44;
      border-color: #0f8b44;
    }
  }
`;